/** @jsxImportSource @emotion/react */
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'components/global/Icon';
import LordIcon from 'components/global/LordIcon';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useUpdateEffect from 'hooks/useUpdateEffect';

import { THEME } from 'utils/Theme';

import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';

import * as S from './styled/Button.styled';

/**
 * @author @buunsal
 * @description Buttons allow users to take actions, and make choices, with a single tap.
 * @param {Object} props - props of the component.
 * @param {'blue' | 'text' | 'outline' | 'black' | 'blackOutline' | 'action' | 'borderless' | 'blueBorderless' | 'transparent'} [props.variant = 'blue'] - Applies the ready to use styles.
 * @param {'x-small' | 'small' | 'medium' | 'large' | 'x-large' | 'xx-large'} [props.size = 'medium'] - The size of the component.
 * @param {boolean} [props.disabled = false] - If true, the button will be disabled.
 * @param {string} [props.disabledTooltip] - The label tooltip of the button when it is disabled.
 * @param {boolean} [props.loading = false] - If true, the component show loading spinner in content area.
 * @param {string} [props.loadingLabel] - Shows loading as a text after icon.
 * @param {Object} [props.loadingIcon] - Icon for loading. Default is faCircleNotch. You can use any icon from FontAwesome.
 * @param {string} [props.className] - Adds classname to the component. Override or extend the styles applied to the component.
 * @param {Object} [props.css] - Adds css to the styled component. Override or extend the styles applied to the component.
 * @param {string} [props.label] - The text content of the component. The component use the i18n translation for the label. If it's empty only icon will be shown.
 * @param {Object} [props.icon] - Icon for the button. You can use any icon from FontAwesome.
 * @param {'small' | 'medium' | 'large'} [props.iconSize] - Icon for the button. You can use any icon from FontAwesome.
 * @param {'left' | 'right'} [props.iconPos = 'left'] - Position of the icon. Shows icon on the left or right side of the label.
 * @param {boolean} [props.iconButton = false] - If true, the component will be icon only button.
 * @param {boolean} [props.fullWidth = false] - If true, the component will be full width.
 * @param {'button', 'label', 'span', 'a'} [props.component = 'button'] -  The component used for the root node. Either a string to use a DOM element or a component.
 * @param {*} [props.children] - The content of the component. If it's empty only label and icon will be shown.
 */
const Button = (props, ref) => {
	const {
		variant = 'solid',
		size = 'medium',
		color = 'var(--blue)',
		disabled,
		disabledTooltip,
		loading,
		loadingTooltip,
		loadingLabel,
		loadingIcon,
		style,
		className,
		css,
		label,
		lordIcon,
		icon,
		iconPos,
		iconSize,
		iconButton,
		fullWidth,
		component = 'button',
		children,
		...moreProps
	} = props;

	const innerRef = useRef(null);
	const [width, setWidth] = useState(0);
	const [isDisabled, setIsDisabled] = useState(loading || disabled);

	useUpdateEffect(() => {
		setIsDisabled(loading || disabled);
	}, [disabled, loading]);

	const enabledProps = !isDisabled ? moreProps : {};

	useImperativeHandle(ref, () => {
		innerRef.current.enable = () => setIsDisabled(false);
		innerRef.current.disable = () => setIsDisabled(true);
		return innerRef.current;
	});

	const createRipple = (event) => {
		const button = event.currentTarget;
		const ButtonClientRect = button.getBoundingClientRect();

		let inkEl = button.querySelector('.ink');

		if (inkEl) {
			inkEl.classList.remove('animate', 'animate-out');
		} else {
			inkEl = document.createElement('span');
			inkEl.classList.add('ink');
			inkEl.style.width = inkEl.style.height = `${Math.max(button.offsetWidth, button.offsetHeight)}px`;
			button.appendChild(inkEl);
		}

		const offsetX = event.pageX - ButtonClientRect.left;
		const offsetY = event.pageY - ButtonClientRect.top;

		inkEl.style.left = `${offsetX - inkEl.offsetWidth / 2}px`;
		inkEl.style.top = `${offsetY - inkEl.offsetHeight / 2}px`;
		inkEl.classList.add('animate');
	};

	const removeRipple = (event) => {
		const button = event.currentTarget;
		const inkEl = button.querySelector('.ink');

		if (inkEl) {
			inkEl.classList.add('animate-out');

			setTimeout(() => {
				inkEl.remove();
			}, 650);
		}
	};

	useEffect(() => {
		if (isDisabled) return;

		const button = innerRef.current;
		button.addEventListener('mousedown', createRipple);
		button.addEventListener('mouseup', removeRipple);
		button.addEventListener('mouseleave', removeRipple);

		return () => {
			button.removeEventListener('mousedown', createRipple);
			button.removeEventListener('mouseup', removeRipple);
			button.removeEventListener('mouseleave', removeRipple);
		};
	}, [isDisabled, innerRef]);

	const id = useId();
	const darkMode = useSelector((state) => state.layout.darkMode);

	useEffect(() => {
		const button = innerRef.current;
		const width = button.offsetWidth;
		!loading && setWidth(width);
	}, [loading]);

	return (
		<>
			<S.Button
				data-id={`button_${label ?? (icon ? icon.name : id)}`}
				type="button"
				buttonRef={innerRef}
				color={color}
				className={`button ${variant} ${size} ${iconButton ? 'iconButton' : ''} ${
					iconPos === 'right' ? 'rightIcon' : ''
				} ${fullWidth ? 'fullWidth' : ''} ${isDisabled ? 'disabled' : ''} button_class_selector_${
					typeof label === 'string' ? label?.replaceAll('.', '_') : icon ? icon.name : id
				} ${className ?? ''}`}
				style={{
					...style,
					...(loading ? { width: width - 1 } : {})
				}}
				css={css}
				tag={component}
				{...enabledProps}>
				{!loading ? (
					<>
						{lordIcon ? (
							<LordIcon
								src={lordIcon}
								color={variant === 'blue' ? THEME.WHITE : ''}
								primaryColor={darkMode ? THEME.WHITE : THEME.BLACK}
								secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
								target={innerRef}
							/>
						) : (
							icon && (
								<Icon
									className="button-icon"
									icon={icon}
									size={iconSize}
									fontSize={!iconButton ? 'var(--paragraph3-font-size)' : undefined}
								/>
							)
						)}

						{label && (
							<Typography className="button-text" fontWeight={500} noWrap>
								{label}
							</Typography>
						)}
						{children ? children : undefined}
					</>
				) : (
					<>
						<Icon icon={loadingIcon ?? faCircleNotch} className="button-icon fa-spin" />
						{loadingLabel && (
							<Typography className="button-text" fontWeight={500}>
								{loadingLabel}
							</Typography>
						)}
					</>
				)}
			</S.Button>
			{isDisabled && !loading && (
				<Tooltip componentRef={innerRef.current} title={disabledTooltip} placement="bottom" />
			)}

			{loading && loadingTooltip && (
				<Tooltip componentRef={innerRef.current} title={loadingTooltip} placement="bottom" />
			)}
		</>
	);
};

Button.displayName = 'Button';

/**
 * @description Buttons allow users to take actions, and make choices, with a single tap.
 * @param {Function} Button
 * @param {Object} Button.props - props of the component.
 * @param {'blue' | 'outline' | 'black' | 'blackOutline' | 'action' | 'borderless' | 'blueBorderless'} [props.variant = 'blue'] - Applies the ready to use styles.
 * @param {'x-small' | 'small' | 'medium' | 'large'} [props.size = 'medium'] - The size of the component.
 * @param {boolean} [props.disabled = false] - If true, the button will be disabled.
 * @param {string} [props.disabledTooltip] - The label tooltip of the button when it is disabled.
 * @param {boolean} [props.loading = false] - If true, the component show loading spinner in content area.
 * @param {string} [props.loadingTooltip] - The label tooltip of the button when it is loading.
 * @param {string} [props.loadingLabel] - Shows loading as a text after icon.
 * @param {Object} [props.loadingIcon] - Icon for loading. Default is faCircleNotch. You can use any icon from FontAwesome.
 * @param {string} [props.className] - Adds classname to the component. Override or extend the styles applied to the component.
 * @param {Object} [props.css] - Adds css to the styled component. Override or extend the styles applied to the component.
 * @param {string} [props.label] - The text content of the component. The component use the i18n translation for the label. If it's empty only icon will be shown.
 * @param {Object} [props.icon] - Icon for the button. You can use any icon from FontAwesome.
 * @param {'small' | 'medium' | 'large'} [props.iconSize] - Icon for the button. You can use any icon from FontAwesome.
 * @param {'left' | 'right'} [props.iconPos = 'left'] - Position of the icon. Shows icon on the left or right side of the label.
 * @param {boolean} [props.iconButton = false] - If true, the component will be icon only button.
 * @param {boolean} [props.fullWidth = false] - If true, the component will be full width.
 * @param {'button', 'label', 'span', 'a'} [props.component = 'button'] -  The component used for the root node. Either a string to use a DOM element or a component.
 * @param {*} [props.children] - The content of the component. If it's empty only label and icon will be shown.
 * @returns {object} - Button object with the given props.
 * @example
 * import Button from 'components/field/Button';
 * import { css } from '@emotion/react';
 *
 * <Button
 * 	variant="borderless"
 * 	size="large"
 * 	disabled={false}
 * 	loading={true}
 * 	loadingLabel="loading"
 * 	loadingIcon={faCircleNotch}
 * 	className="redButton"
 * 	css={css`background-color: red !important;`}
 * 	label="text"
 * 	icon={faCircleNotch}
 * 	iconPos="right"
 * />
 */
export default forwardRef(Button);
